import { ColorDot } from '@/components/ui/color-dot'
import { cn, formatDateStr, formatAmount } from '@/lib/utils'

type Variant = 'dots' | 'heatmap'

const HEAT_CLASSES: Record<number, string> = {
  0: 'bg-muted',
  1: 'bg-orange-200 dark:bg-orange-900/60',
  2: 'bg-orange-300 dark:bg-orange-700/70',
  3: 'bg-orange-400 dark:bg-orange-600/80',
  4: 'bg-orange-500 dark:bg-orange-500',
}

function getHeatLevel(
  amount: number,
  quartiles: readonly [number, number, number] | null,
): 0 | 1 | 2 | 3 | 4 {
  if (amount === 0 || quartiles === null) return 0
  const [q1, q2, q3] = quartiles
  if (amount <= q1) return 1
  if (amount <= q2) return 2
  if (amount <= q3) return 3
  return 4
}

export interface DayData {
  date: Date
  dateStr: string
  amount: number
  colors: string[]
  isCurrentMonth: boolean
  isToday: boolean
  dayOfMonth: number
}

export function buildDayData(
  start: Date,
  count: number,
  dailyTotals: Map<string, number>,
  dailyCategoryColors: Map<string, string[]>,
  monthYear: number,
  monthNum: number,
  todayStr: string,
): DayData[] {
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(start)
    date.setDate(date.getDate() + i)
    const dateStr = formatDateStr(date)
    const amount = dailyTotals.get(dateStr) ?? 0
    const colors = dailyCategoryColors.get(dateStr) ?? []
    const isCurrentMonth = date.getFullYear() === monthYear && date.getMonth() + 1 === monthNum
    const isToday = dateStr === todayStr
    return { date, dateStr, amount, colors, isCurrentMonth, isToday, dayOfMonth: date.getDate() }
  })
}

export { type Variant }

interface DayCellProps {
  day: DayData
  variant: Variant
  heatmapQuartiles: readonly [number, number, number] | null
  onClick?: () => void
}

export function DayCell({ day, variant, heatmapQuartiles, onClick }: DayCellProps) {
  const heatBg = variant === 'heatmap' && day.isCurrentMonth
    ? HEAT_CLASSES[getHeatLevel(day.amount, heatmapQuartiles)]
    : 'bg-muted'
  const hasAmount = day.isCurrentMonth && day.amount > 0
  const hasDots = variant === 'dots' && day.isCurrentMonth && day.colors.length > 0

  return (
    <div
      className={cn(
        'flex aspect-square flex-col items-center justify-center gap-0.5 rounded-md',
        heatBg,
        day.isToday && 'ring-2 ring-primary',
        !day.isCurrentMonth && 'opacity-30',
        onClick && 'cursor-pointer active:scale-95',
      )}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
    >
      <span className="text-xs font-medium">{day.dayOfMonth}</span>
      <span className={cn(
        'text-[10px] leading-tight text-foreground/80',
        !hasAmount && 'invisible',
      )}>
        {hasAmount ? formatAmount(day.amount, 'JPY', true) : '\u00A0'}
      </span>
      <div className={cn(
        'flex flex-wrap justify-center gap-0.5',
        !hasDots && 'invisible',
      )}>
        {day.colors.length > 0
          ? day.colors.map((color) => (
              <ColorDot key={color} color={color} size="xs" />
            ))
          : <span className="inline-block size-1.5" />}
      </div>
    </div>
  )
}
